import i18n from 'i18next';
import { ESTADOS_EUA, PROVINCIAS_ES, SIGLA_DOCUMENTO, tiposDoPais, type NaturezaDocumento, type TipoDocumentoPais } from '@malgoos/shared';
import type { Opcao } from '../componentes/formulario';

/** As 27 unidades da federação (fase 2, W5): no BR a UF é uma lista, nunca texto livre. */
export const UFS = ['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'] as const;

/** Países ISO 3166-1 alfa-2 (o nome vem do navegador, no idioma do app, por `Intl.DisplayNames`). */
const PAISES =
  'AD AE AF AG AI AL AM AO AR AT AU AW AZ BA BB BD BE BF BG BH BI BJ BM BN BO BR BS BT BW BY BZ CA CD CF CG CH CI CL CM CN CO CR CU CV CW CY CZ DE DJ DK DM DO DZ EC EE EG ER ES ET FI FJ FM FR GA GB GD GE GH GI GM GN GQ GR GT GW GY HK HN HR HT HU ID IE IL IN IQ IR IS IT JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MG MH MK ML MM MN MO MR MT MU MV MW MX MY MZ NA NE NG NI NL NO NP NR NZ OM PA PE PG PH PK PL PR PS PT PW PY QA RO RS RU RW SA SB SC SD SE SG SI SK SL SM SN SO SR SS ST SV SY SZ TD TG TH TJ TL TM TN TO TR TT TV TW TZ UA UG US UY UZ VA VC VE VN VU WS XK YE ZA ZM ZW'.split(
    ' ',
  );

/** Moedas mais usadas por quem vende ou compra fora do Brasil (ISO 4217). */
const MOEDAS = ['USD', 'EUR', 'GBP', 'ARS', 'CLP', 'COP', 'MXN', 'PYG', 'UYU', 'PEN', 'BOB', 'CAD', 'CHF', 'JPY', 'CNY', 'AUD', 'BRL'];

const locale = () => ({ pt: 'pt-BR', en: 'en-US', es: 'es-ES' })[(i18n.resolvedLanguage ?? i18n.language ?? 'pt').slice(0, 2) as 'pt' | 'en' | 'es'] ?? 'pt-BR';

function nomeIntl(tipo: 'region' | 'currency', codigo: string): string {
  try {
    return new Intl.DisplayNames([locale()], { type: tipo }).of(codigo) ?? codigo;
  } catch {
    return codigo;
  }
}

/** Nome do país no idioma do app; código desconhecido volta como veio. */
export const nomePais = (codigo: string | null | undefined) => (codigo ? nomeIntl('region', codigo) : '');

/** Opções de país em ordem alfabética do idioma, com o Brasil primeiro. */
export function opcoesPais(): Opcao[] {
  const col = new Intl.Collator(locale());
  const outros = PAISES.filter((p) => p !== 'BR')
    .map((p) => ({ valor: p, rotulo: nomePais(p) }))
    .sort((a, b) => col.compare(a.rotulo, b.rotulo));
  return [{ valor: 'BR', rotulo: nomePais('BR') }, ...outros];
}

export const opcoesMoeda = (): Opcao[] => MOEDAS.map((m) => ({ valor: m, rotulo: `${m} · ${nomeIntl('currency', m)}` }));

export const opcoesUf = (): Opcao[] => UFS.map((u) => ({ valor: u, rotulo: u }));

// ------------------------------------------------------------------------------------------------- lote 19/10 (países)

/** Os 50 estados dos EUA e DC, pela sigla (a mesma do endereço). */
export const opcoesEstadoEua = (): Opcao[] => ESTADOS_EUA.map((u) => ({ valor: u, rotulo: u }));

/** As 52 províncias da Espanha pelo nome, com o código ISO 3166-2:ES como valor (o que vai no `uf`). */
export function opcoesProvinciaEs(): Opcao[] {
  const col = new Intl.Collator('es-ES');
  return [...PROVINCIAS_ES].sort((a, b) => col.compare(a.nome, b.nome)).map((p) => ({ valor: p.codigo, rotulo: p.nome }));
}

/** Nome da província pelo código ISO (para a lista); código desconhecido volta como veio. */
export const nomeProvinciaEs = (codigo: string | null | undefined) => PROVINCIAS_ES.find((p) => p.codigo === codigo)?.nome ?? codigo ?? '';

/** Moedas da empresa do grupo (real, dólar e euro). */
export const opcoesMoedaEmpresa = (): Opcao[] => ['BRL', 'USD', 'EUR'].map((m) => ({ valor: m, rotulo: `${m} · ${nomeIntl('currency', m)}` }));

/** Rótulo do tipo de documento no idioma (EIN, SSN, NIF...); tipo digitado de outro país volta como veio. */
export const rotuloTipoDocumento = (tipo: string | null | undefined): string => (tipo && tipo in SIGLA_DOCUMENTO ? i18n.t(`documento.tipos.${tipo}`) : (tipo ?? ''));

/** Opções do tipo de documento do país para empresa ou pessoa (vazio fora de BR, EUA e Espanha). */
export const opcoesTipoDocumento = (pais: string | null | undefined, natureza: NaturezaDocumento): Opcao[] =>
  tiposDoPais(pais, natureza).map((t: TipoDocumentoPais) => ({ valor: t, rotulo: rotuloTipoDocumento(t) }));
